/**
 * LoadingScreen Component
 * 
 * Beautiful loading screen dengan:
 * - Custom icon support
 * - Progress bar
 * - Current asset loading display
 * - Blur fade animations
 */

import React from 'react'

export default function LoadingScreen({
  allAssetsLoaded,
  loadingIconFile,
  loadingStatus,
  currentAssetLoading,
  currentAssetProgress,
}) {
  if (allAssetsLoaded) return null

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        animation: allAssetsLoaded ? 'fadeOut 0.5s ease-out forwards' : 'none',
      }}
    >
      {/* Logo/Icon */}
      <div
        style={{
          width: '80px',
          height: '80px',
          borderRadius: '20px',
          background:
            'linear-gradient(135deg, rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.08))',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '32px',
          animation: 'pulse-glow 2s ease-in-out infinite',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        {loadingIconFile ? (
          <img
            src={loadingIconFile}
            alt="Loading Icon"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              padding: '12px',
            }}
          />
        ) : (
          <div style={{ fontSize: '40px' }}>🎬</div>
        )}
      </div>

      {/* Spinner */}
      <div
        style={{
          width: '48px',
          height: '48px',
          border: '3px solid rgba(0, 0, 0, 0.1)',
          borderTop: '3px solid rgba(0, 0, 0, 0.8)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          marginBottom: '24px',
        }}
      />

      {/* Title */}
      <div
        style={{
          color: '#000000',
          fontSize: '16px',
          fontWeight: 600,
          marginBottom: '4px',
          letterSpacing: '0.5px',
        }}
      >
        River Visual 3D Engine™
      </div>

      <div
        style={{
          color: 'rgba(0, 0, 0, 0.7)',
          fontSize: '14px',
          fontWeight: 500,
          marginBottom: '16px',
        }}
      >
        Launching the Scene
      </div>

      {/* Current Asset */}
      {currentAssetLoading && (
        <div
          key={currentAssetLoading}
          style={{
            color: '#000000',
            fontSize: '14px',
            fontWeight: 600,
            marginBottom: '4px',
            display: 'flex',
            gap: '6px',
          }}
        >
          <span>📦</span>
          {currentAssetLoading.split(' ').map((word, index) => (
            <span key={`${word}-${index}`}>{word}</span>
          ))}
        </div>
      )}

      {/* Status */}
      <div
        style={{
          color: 'rgba(0, 0, 0, 0.65)',
          fontSize: '13px',
          marginBottom: '4px',
        }}
      >
        {loadingStatus}
      </div>

      {/* Percentage */}
      {currentAssetProgress > 0 && (
        <div
          style={{
            color: 'rgba(0, 0, 0, 0.8)',
            fontSize: '20px',
            fontWeight: 700,
            marginTop: '8px',
            marginBottom: '16px',
          }}
        >
          {currentAssetProgress}%
        </div>
      )}

      {/* Progress Bar */}
      <div
        style={{
          width: '300px',
          height: '6px',
          background: 'rgba(0, 0, 0, 0.1)',
          borderRadius: '3px',
          marginTop: '16px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width:
              currentAssetProgress > 0 ? `${currentAssetProgress}%` : '100%',
            height: '100%',
            background:
              currentAssetProgress > 0
                ? 'linear-gradient(90deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.9))'
                : 'linear-gradient(90deg, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.8))',
            animation:
              currentAssetProgress > 0 ? 'none' : 'slideInFromLeft 1.5s ease-in-out infinite',
            transition: 'width 0.3s ease-out',
            borderRadius: '3px',
          }}
        />
      </div>

      {/* Hint */}
      <div
        style={{
          marginTop: '32px',
          padding: '16px 24px',
          background: 'rgba(0, 0, 0, 0.08)',
          borderRadius: '12px',
          border: '1px solid rgba(0, 0, 0, 0.15)',
          maxWidth: '350px',
        }}
      >
        <div
          style={{
            color: 'rgba(0, 0, 0, 0.75)',
            fontSize: '12px',
            lineHeight: '1.6',
            textAlign: 'center',
          }}
        >
          💡 Loading all creative assets before viewport opens…
          <br />
          Ensuring a seamless visual experience.
        </div>
      </div>
    </div>
  )
}

